import * as React from "react";
import {
  Button,
  ButtonGroup,
  Dropdown,
  Form,
  Spinner,
  SplitButton,
} from "react-bootstrap";
import I18n from "lib/I18n";
import { convertDurationToReadable } from "lib/converter";
import { exportFile } from "lib/components/ExportFile";
import { supportUrl } from "lib/constants";
import { Status } from "../../constants";
import moment from "moment";
import "moment/dist/locale/ja";
import style from "./style.module.css";
import { BsExclamationTriangleFill } from "react-icons/bs";
import { observer } from "mobx-react-lite";
import { ColumnField, QueryResult, getStore } from "../../store";
import { Spreadsheet } from "lib/components/Spreadsheet";
import { useRef } from "react";

interface CheckDropdownItemProps {
  label: React.ReactNode;
  id: string;
  checked: boolean;
  indeterminate: boolean;
  onChange?: (id: string) => void;
}

const CheckDropdownItem = React.forwardRef<
  HTMLDivElement,
  CheckDropdownItemProps
>(({ label, id, checked, indeterminate, onChange }, ref) => {
  const checkRef = useRef<HTMLInputElement>(null);
  const depth = id.split(".").length - 1;
  const indent = depth * 20;

  if (checkRef.current) {
    checkRef.current.indeterminate = indeterminate;
  }

  const onClick = (e: React.MouseEvent) => {
    if (
      e.target instanceof HTMLLabelElement ||
      e.target instanceof HTMLInputElement
    ) {
      return;
    }
    if (onChange) {
      onChange(id);
    }
  };

  return (
    <Form.Group
      ref={ref}
      className="dropdown-item mb-0"
      controlId={id}
      onClick={onClick}
    >
      <Form.Check
        type="checkbox"
        label={label}
        checked={checked}
        ref={checkRef}
        style={{ marginLeft: indent }}
        onChange={() => {
          if (onChange) {
            onChange(id);
          }
        }}
      />
    </Form.Group>
  );
});

enum Visibility {
  All = "all",
  Some = "some",
  None = "none",
}

function computeVisibility(
  queryResult: QueryResult,
  columnField: ColumnField,
): Visibility {
  if (columnField.children.length == 0) {
    if (queryResult.visibleColumns.includes(columnField.id)) {
      return Visibility.All;
    } else {
      return Visibility.None;
    }
  }
  let visibilityCount = 0;
  for (const child of columnField.children) {
    const visibility = computeVisibility(queryResult, child);
    switch (visibility) {
      case Visibility.Some:
        return Visibility.Some;
      case Visibility.All:
        visibilityCount++;
        break;
    }
  }
  if (visibilityCount === columnField.children.length) {
    return Visibility.All;
  } else if (visibilityCount > 0) {
    return Visibility.Some;
  } else {
    return Visibility.None;
  }
}

function getColumnsFromField(columnFields: ColumnField[]): string[] {
  const ret: string[] = [];
  for (const columnField of columnFields) {
    if (columnField.children.length > 0) {
      ret.push(...getColumnsFromField(columnField.children));
    } else {
      ret.push(columnField.id);
    }
  }
  return ret;
}

interface ColumnFieldMenuItemProps {
  queryResult: QueryResult;
  columnField: ColumnField;
}

const ColumnFieldMenuItem = observer((props: ColumnFieldMenuItemProps) => {
  const { queryResult, columnField } = props;
  const visibility = computeVisibility(queryResult, columnField);
  const checked =
    visibility === Visibility.All || visibility === Visibility.Some;
  const indeterminate = visibility === Visibility.Some;

  const onChange = (id: string) => {
    if (columnField.children.length > 0) {
      const allColumns = getColumnsFromField(columnField.children);
      if (checked) {
        queryResult.visibleColumns = queryResult.visibleColumns.filter(
          (_) => !allColumns.includes(_),
        );
      } else {
        queryResult.visibleColumns.push(...allColumns);
      }
    } else {
      const index = queryResult.visibleColumns.indexOf(id);
      if (index === -1) {
        queryResult.visibleColumns.push(id);
      } else {
        queryResult.visibleColumns.splice(index, 1);
      }
    }
  };

  return (
    <>
      <CheckDropdownItem
        id={columnField.id}
        label={columnField.name}
        checked={checked}
        indeterminate={indeterminate}
        onChange={onChange}
      />
      {columnField.children.map((columnField) => (
        <ColumnFieldMenuItem
          key={columnField.id}
          queryResult={queryResult}
          columnField={columnField}
        />
      ))}
    </>
  );
});

interface QueryResultProps {
  queryResult: QueryResult;
  onCreateGraph: () => void;
}

export const QueryResultItem = observer((props: QueryResultProps) => {
  const { queryResult, onCreateGraph } = props;
  const app = getStore().app;

  const onExportCsv = () => {
    const csv = queryResult.getCsv();
    exportFile(csv, `${queryResult.queryId}.csv`, "text/csv");
  };

  const renderTable = (csvHeader: string[], csvBody: string[][]) => {
    const hiddenColumns = csvHeader.filter(
      (_) => !queryResult.visibleColumns.includes(_),
    );

    return (
      <Spreadsheet
        data={csvBody}
        columns={csvHeader}
        rowHeaders={false}
        hiddenColumns={hiddenColumns}
        readOnly
      />
    );
  };

  const renderResult = () => {
    if (queryResult.queryStatus === Status.Success) {
      if (queryResult.isLoading) {
        return (
          <div className={style.status}>
            {I18n.t("bigquery_query_viewer.messages.loading_result")}
          </div>
        );
      }
      if (
        queryResult.csvHeader != null &&
        queryResult.csvBody != null &&
        queryResult.csvBody.length > 0
      ) {
        return renderTable(queryResult.csvHeader, queryResult.csvBody);
      }
      return (
        <div className={style.status}>
          <span className="text-muted">
            {I18n.t("bigquery_query_viewer.messages.result_empty")}
          </span>
        </div>
      );
    }
    const status = queryResult.queryStatus;
    return (
      <div className={style.status}>
        {I18n.t(`bigquery_query_viewer.messages.status_${status}`)}
        {renderAlert()}
      </div>
    );
  };

  const renderAlert = () => {
    if (!queryResult.queryMessage) {
      return;
    }
    return (
      <div className="alert alert-danger" style={{ marginBottom: 0 }}>
        <pre className="text-danger">
          <code>{queryResult.queryMessage}</code>
        </pre>
        <BsExclamationTriangleFill />
        上記のエラー内容で問題が解決しない場合は
        <a href={supportUrl} target="_blank" rel="noreferrer">
          サポート
        </a>
        に問い合わせてください。サポート番号:
        {queryResult.queryId}
      </div>
    );
  };

  const renderDownload = () => {
    const isExporting =
      queryResult.exportStatus != Status.WaitingEnqueue &&
      queryResult.exportStatus != Status.Success;

    return (
      <SplitButton
        size="sm"
        variant="success"
        title={
          <>
            {isExporting ? <Spinner animation="border" size="sm" /> : undefined}
            {I18n.t("bigquery_query_viewer.label.download")}
          </>
        }
        style={{ marginRight: 10 }}
        disabled={
          queryResult.isLoading || queryResult.csv == null || isExporting
        }
        onClick={onExportCsv}
      >
        <Dropdown.Item onClick={onExportCsv}>
          {I18n.t("bigquery_query_viewer.label.download_10000", {
            default_limit: 10000,
          })}
        </Dropdown.Item>
        <Dropdown.Item
          disabled={
            // 10000件未満
            (queryResult.csv?.length ?? 0) < 10000 &&
            // 24時間以降
            moment
              .duration(moment().diff(moment(queryResult.enqueuedAt)))
              .asHours() < 24
          }
          onClick={() => queryResult.exportQuery(app.titleUuid, app.env)}
        >
          {I18n.t("bigquery_query_viewer.label.download_entire")}
        </Dropdown.Item>
      </SplitButton>
    );
  };

  const renderStop = () => {
    return (
      <Button
        size="sm"
        variant="danger"
        disabled={queryResult.queryStatus !== Status.Running}
        style={{ marginRight: 10 }}
        onClick={() => queryResult.stopQuery(app.titleUuid, app.env)}
      >
        {I18n.t("bigquery_query_viewer.label.stop_query")}
      </Button>
    );
  };

  const renderCreateGraph = () => {
    return (
      <Button
        size="sm"
        variant="light"
        disabled={queryResult.queryStatus !== Status.Success}
        style={{ marginRight: 10 }}
        onClick={onCreateGraph}
      >
        {I18n.t("bigquery_query_viewer.label.create_graph")}
      </Button>
    );
  };

  const renderExpandJson = () => {
    return (
      <Form.Check
        type="switch"
        id="expand-json"
        label={I18n.t("bigquery_query_viewer.label.expand_json")}
        checked={queryResult.expandJson}
        disabled={queryResult.queryStatus !== Status.Success}
        style={{ display: "inline-block" }}
        onChange={(e) => (queryResult.expandJson = e.currentTarget.checked)}
      />
    );
  };

  const renderHiddenColumns = () => {
    const onSelectAll = () => {
      if (!queryResult.csv) {
        return;
      }
      queryResult.visibleColumns = queryResult.csv[0].slice();
    };

    const onSelectNone = () => {
      queryResult.visibleColumns = [];
    };

    return (
      <Dropdown style={{ display: "inline-block", marginRight: 10 }}>
        <Dropdown.Toggle
          size="sm"
          variant="light"
          id="visible-columns-dropdown"
        >
          {I18n.t("bigquery_query_viewer.label.column_visibility_setting")}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: 300, overflowY: "auto" }}>
          <ButtonGroup size="sm">
            <Button variant="link" onClick={onSelectAll}>
              {I18n.t("bigquery_query_viewer.label.select_all")}
            </Button>
            <Button variant="link" onClick={onSelectNone}>
              {I18n.t("bigquery_query_viewer.label.unselect_all")}
            </Button>
          </ButtonGroup>
          <Dropdown.Divider />
          {queryResult.columnFields.map((columnField) => (
            <ColumnFieldMenuItem
              key={columnField.id}
              queryResult={queryResult}
              columnField={columnField}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderStatus = () => {
    const status = queryResult.queryStatus;
    const message = I18n.t(`bigquery_query_viewer.label.status_${status}`);
    if (message) {
      return `[${message}] `;
    }
    return "";
  };

  const renderExecutionTime = () => {
    return (
      <span>
        {I18n.t("bigquery_query_viewer.label.execution_time")}:{" "}
        {queryResult.executionTime
          ? convertDurationToReadable(queryResult.executionTime)
          : "N/A"}
      </span>
    );
  };

  const renderCost = () => {
    return (
      <span>
        {I18n.t("bigquery_query_viewer.label.cost")}:{" "}
        {queryResult.cost ? `$${queryResult.cost.toPrecision(3)}` : "N/A"}
      </span>
    );
  };

  const renderRequestTime = () => {
    return (
      <span>
        {I18n.t("bigquery_query_viewer.label.enqueued_at")}:{" "}
        {moment(queryResult.enqueuedAt).format("YYYY-MM-DD HH:mm:ss")}
      </span>
    );
  };

  return (
    <div className={style.root}>
      <div className={style.header}>
        <div>
          {renderStatus()}
          {renderStop()}
          {renderDownload()}
          {renderCreateGraph()}
          {renderHiddenColumns()}
          {renderExpandJson()}
        </div>
        <div>
          {renderExecutionTime()}
          <span className={style.verticalSeparator} />
          {renderCost()}
          <span className={style.verticalSeparator} />
          {renderRequestTime()}
        </div>
      </div>
      <div className={style.spreadsheet}>{renderResult()}</div>
    </div>
  );
});
